import { CONTACT_RECORD_FLOATS } from './contactRecord';
import { JOINT_RECORD_FLOATS } from './jointRecord';
import { SPRING_RECORD_FLOATS } from './springRecord';
import {
  planCompactContactPoolLayout,
  runCompactContactPoolSelfTests,
} from './compactContactPool';
import { runContactOnlyCsrSelfTests } from './contactOnlyCsr';
import {
  runContactOnlyCsrGpuSelfTests,
  type ContactOnlyCsrGpuSelfTestResult,
} from './contactOnlyCsrGpu';
import {
  runContactOnlyCsrGpuRuntimeSelfTests,
  type ContactOnlyCsrGpuRuntimeSelfTestResult,
} from './contactOnlyCsrGpuRuntime';
import {
  runCanonicalContactStorageLayoutSelfTests,
  type CanonicalContactStorageSelfTestResult,
} from './contactSoaLayout';
import {
  LEGACY_PACKED_PAIR_MAX_BODY_INDEX,
  WIDE_PAIR_MAX_BODY_INDEX,
  planPairActivityLayout,
  runPairAbiSelfTests,
  type PairEndpointEncoding,
  type PairActivityLayout,
} from './pairAbi';
import {
  runWideCompactContactPoolSelfTests,
  type WideCompactContactPoolSelfTestResult,
} from './wideCompactContactPool';
import {
  runWideExactContactGenerationSelfTests,
  type WideExactContactGenerationSelfTestResult,
} from './wideExactContactGeneration';

const BYTES_PER_WORD = 4;
const CANDIDATE_WORKGROUP_SIZE = 64;
const DEFAULT_MAX_COMPUTE_WORKGROUPS_PER_DIMENSION = 65_535;
const DEFAULT_MAX_JOINTS_PER_BODY_SOLVER = 8;
const DEFAULT_MAX_SPRINGS_PER_BODY_SOLVER = 12;
const IGNORED_PAIR_HASH_ENTRY_WORDS = 2;
const INERTIAL_POSE_FLOATS_PER_BODY = 20;
const PERSISTENT_MANIFOLD_HEADER_WORDS = 4;
const LEGACY_PERSISTENT_MANIFOLD_POOL_ARRAY_COUNT = 4;
const COMPACT_PERSISTENT_MANIFOLD_LIFECYCLE_ARRAY_COUNT = 3;

export type CapacityBufferDomain = 'cpu' | 'gpu';
export type CapacityElementType = 'float32' | 'uint32';
export type PhysicsEngineCapacityMode = 'legacy' | 'optimized';
export type CapacityGpuOnlyBufferKey =
  | 'pairContacts'
  | 'jointRecords'
  | 'springRecords'
  | 'pairActivity'
  | 'pairVisitedBits'
  | 'pairBodyContactCounts'
  | 'pairBodyContactIndices'
  | 'bodyConstraintCounts'
  | 'bodyConstraintRefs'
  | 'ignoredPairHash'
  | 'persistentManifoldMap'
  | 'pairColorBodyClaims'
  | 'sleepingCachedBodyRoot'
  | 'sleepingCachedPairKey'
  | 'sleepingCacheSeenBits'
  | 'sleepingCachePairSeenBits';

/**
 * Target allocation policy used only by this dry-run planner. Selecting the
 * optimized model does not claim that a PhysicsEngine instance has installed
 * the corresponding GPU-only or aliased attributes.
 */
export interface PhysicsEngineCapacityAllocationOptions {
  /** Defaults to `legacy`, preserving the original fixed-slot calculation. */
  mode?: PhysicsEngineCapacityMode;
  /** Capacity of the single physical compact contact-row pool. */
  compactContactPoolCapacity?: number;
  /** Live, physically allocated record capacities; both must stay <= max*. */
  allocatedJoints?: number;
  allocatedSprings?: number;
  /**
   * Keep solver adjacency/spill budgets at logical maxJoints/maxSprings while
   * joint/spring record buffers use the live physical allocation. This is the
   * runtime-growth policy installed by PhysicsEngine.
   */
  constraintGraphUsesLogicalMax?: boolean;
  /**
   * Alias bodyConstraintRefs to pairBodyContactIndices. This is valid only for
   * a truly contact-only configuration (maxJoints=maxSprings=0).
   */
  contactOnlyAdjacencyAlias?: boolean;
  /** Include contact-only GPU sleeping topology-cache allocations. */
  includeSleepingTopologyCache?: boolean;
  /**
   * Allocate the dense pair-visited bitset. Defaults to true. A disabled path
   * retains one u32 sentinel because WebGPU storage bindings cannot be empty.
   */
  enableBroadphasePairDedup?: boolean;
  /**
   * Buffers whose CPU typed array is only the one-item placeholder required by
   * createGpuOnlyStorageAttribute. GPU byte counts remain unchanged.
   */
  gpuOnlyBufferKeys?: readonly CapacityGpuOnlyBufferKey[];
}

export interface NormalizedPhysicsEngineCapacityAllocationOptions {
  mode: PhysicsEngineCapacityMode;
  compactContactPoolCapacity: number;
  contactPoolRowCapacity: number;
  allocatedJoints: number;
  allocatedSprings: number;
  constraintGraphUsesLogicalMax: boolean;
  contactOnlyAdjacencyAlias: boolean;
  includeSleepingTopologyCache: boolean;
  enableBroadphasePairDedup: boolean;
  gpuOnlyBufferKeys: readonly CapacityGpuOnlyBufferKey[];
}

/**
 * GPU-generated storage in the optimized reference model. Records populated
 * from scene/runtime CPU data (body state, joint/spring records and ignored
 * pairs) deliberately remain mirrored.
 */
export const CAPACITY_PLANNER_OPTIMIZED_GPU_ONLY_BUFFER_KEYS = Object.freeze([
  'pairContacts',
  'pairActivity',
  'pairVisitedBits',
  'pairBodyContactCounts',
  'pairBodyContactIndices',
  'bodyConstraintCounts',
  'bodyConstraintRefs',
  'persistentManifoldMap',
  'pairColorBodyClaims',
] as const satisfies readonly CapacityGpuOnlyBufferKey[]);

/**
 * The inputs that determine PhysicsEngine's central typed-array/storage-buffer
 * allocations. Defaults and clamps intentionally mirror PhysicsEngine.
 */
export interface PhysicsEngineCapacityInput {
  maxBodies: number;
  /**
   * Candidate endpoint ABI. Omitted selects packed-u16 through 65,536 bodies
   * and exact wide-u32 above that boundary.
   */
  pairEndpointEncoding?: PairEndpointEncoding;
  maxPairsPerBodyBroadphase?: number;
  maxPersistentManifolds?: number;
  pairManifoldSlots?: number;
  maxContactsPerBodySolver?: number;
  maxJoints?: number;
  maxSprings?: number;
  maxIgnoredPairs?: number;
}

/** Only limits used by the central-buffer planner are required. */
export interface CapacityDeviceLimits {
  maxStorageBufferBindingSize: number;
  maxBufferSize: number;
  maxComputeWorkgroupsPerDimension?: number;
}

export interface NormalizedPhysicsEngineCapacityInput {
  maxBodies: number;
  pairEndpointEncoding: PairEndpointEncoding;
  maxPairsPerBodyBroadphase: number;
  maxPersistentManifolds: number;
  pairManifoldSlots: number;
  maxContactsPerBodySolver: number;
  maxJoints: number;
  maxSprings: number;
  maxIgnoredPairs: number;
}

export interface PhysicsEngineCapacityDerived {
  maxPairs: number;
  maxCandidatePairs: number;
  /** Legacy fixed-slot upper bound, retained for compatibility diagnostics. */
  maxPairContacts: number;
  /** Rows available in the active single compact pool. */
  maxActivePairContacts: number;
  compactContactPoolCapacity: number;
  /** Physical rows in pairContactsAttr. */
  contactPoolRowCapacity: number;
  persistentManifoldHashCapacity: number;
  persistentManifoldPoolArrayCount: number;
  ignoredPairHashCapacity: number;
  maxJointsPerBodySolver: number;
  maxSpringsPerBodySolver: number;
  maxConstraintsPerBodySolver: number;
  maxBodyContactOverflowNodes: number;
  maxBodyConstraintOverflowNodes: number;
  storageMaxJoints: number;
  storageMaxSprings: number;
  allocatedJoints: number;
  allocatedSprings: number;
  contactOnlyAdjacencyAliased: boolean;
  pairActivityWordCount: number;
  /** Candidate/active-stream layout sourced from the selected pair ABI. */
  pairActivityLayout: PairActivityLayout;
  pairVisitedWordCount: number;
  pairBodyContactIndicesWordCount: number;
  bodyConstraintRefsWordCount: number;
  persistentManifoldMapWordCount: number;
  sleepingCachePairHashCapacity: number;
  sleepingCacheRowSeenWordCount: number;
  sleepingCachePairSeenWordCount: number;
  /** Sum of the physical-row and canonical-pair seen bitsets. */
  sleepingCacheSeenWordCount: number;
}

export interface CapacityBufferDetail {
  /** PhysicsEngine field/attribute stem, kept stable for diagnostics. */
  key: string;
  label: string;
  domain: CapacityBufferDomain;
  elementType: CapacityElementType;
  elementCount: number;
  bytes: number;
  /** Full logical element count represented by a GPU-only CPU placeholder. */
  logicalElementCount?: number;
  /** True only for the one-item CPU array backing GPU-only storage. */
  gpuOnlyPlaceholder?: boolean;
  /** CPU-only reset snapshots do not have a corresponding GPU allocation. */
  cpuOnly?: boolean;
  /** Named typed-array/storage-attribute views sharing this allocation. */
  aliases?: readonly string[];
}

export interface CapacityLimitStatus {
  checked: boolean;
  ok: boolean;
  maxStorageBufferBindingSize?: number;
  maxBufferSize?: number;
  effectiveStorageBufferLimit?: number;
  violations: CapacityLimitViolation[];
  warnings: string[];
}

export interface CapacityLimitViolation {
  bufferKey: string;
  bufferLabel: string;
  bytes: number;
  exceedsMaxStorageBufferBindingSize: boolean;
  exceedsMaxBufferSize: boolean;
  message: string;
  suggestion: string;
}

export interface PhysicsEngineCapacityPlan {
  input: NormalizedPhysicsEngineCapacityInput;
  allocation: NormalizedPhysicsEngineCapacityAllocationOptions;
  derived: PhysicsEngineCapacityDerived;
  cpuBuffers: CapacityBufferDetail[];
  gpuBuffers: CapacityBufferDetail[];
  totalCpuBytes: number;
  totalGpuBytes: number;
  totalCentralBytes: number;
  largestCpuBuffer: CapacityBufferDetail;
  largestGpuBuffer: CapacityBufferDetail;
  limitStatus: CapacityLimitStatus;
}

export interface CapacityPlannerSelfTestResult {
  ok: boolean;
  /** Future exact-wide layout diagnostic; production storage remains legacy AoS. */
  exactContactSoa: CanonicalContactStorageSelfTestResult['coliseum510k'];
  /** Stable GPU CSR build diagnostic for the exact-wide migration path. */
  exactContactCsrGpu: ContactOnlyCsrGpuSelfTestResult['coliseum510k'];
  /** Reusable exact CSR pipeline/binding/dispatch host-runtime diagnostic. */
  exactContactCsrGpuRuntime: ContactOnlyCsrGpuRuntimeSelfTestResult['coliseum510k'];
  /** Exact endpoint-identity persistent-map diagnostic. */
  exactWidePersistent: WideCompactContactPoolSelfTestResult['coliseum510k'];
  /** Transactional wide row-lifecycle and narrowphase pass-contract diagnostic. */
  exactWideContactGeneration: WideExactContactGenerationSelfTestResult['coliseum510k'];
  profiles: Array<{
    name: string;
    mode: PhysicsEngineCapacityMode;
    totalCpuBytes: number;
    totalGpuBytes: number;
    totalCentralBytes: number;
    largestGpuBufferBytes: number;
    contactPoolRowCapacity: number;
  }>;
  failures: string[];
}

function integerAtLeast(value: number | undefined, fallback: number, minimum: number): number {
  const selected = value ?? fallback;
  if (!Number.isFinite(selected)) {
    throw new Error(`Capacity value must be finite (received ${String(selected)}).`);
  }
  const result = Math.max(minimum, Math.floor(selected));
  if (!Number.isSafeInteger(result)) {
    throw new Error(`Capacity value is outside JavaScript's exact integer range (${selected}).`);
  }
  return result;
}

function checkedProduct(label: string, ...factors: number[]): number {
  let result = 1;
  for (const factor of factors) {
    result *= factor;
    if (!Number.isSafeInteger(result)) {
      throw new Error(`${label} exceeds JavaScript's exact integer range.`);
    }
  }
  return result;
}

function checkedSum(label: string, ...terms: number[]): number {
  const result = terms.reduce((sum, term) => sum + term, 0);
  if (!Number.isSafeInteger(result)) {
    throw new Error(`${label} exceeds JavaScript's exact integer range.`);
  }
  return result;
}

function nextPowerOfTwo(value: number, label: string): number {
  const target = Math.max(1, Math.ceil(value));
  // This is the same upper bound used by PhysicsEngine's sparse hash helper.
  if (target > 0x40000000) {
    throw new Error(`${label} is too large (${target} entries requested).`);
  }
  let result = 1;
  while (result < target) result *= 2;
  return result;
}

function makeBuffer(
  domain: CapacityBufferDomain,
  key: string,
  label: string,
  elementType: CapacityElementType,
  elementCount: number,
  options: Pick<
    CapacityBufferDetail,
    'cpuOnly' | 'aliases' | 'logicalElementCount' | 'gpuOnlyPlaceholder'
  > = {},
): CapacityBufferDetail {
  if (!Number.isSafeInteger(elementCount) || elementCount < 0) {
    throw new Error(`${label} has an invalid element count (${elementCount}).`);
  }
  return {
    key,
    label,
    domain,
    elementType,
    elementCount,
    bytes: checkedProduct(`${label} byte length`, elementCount, BYTES_PER_WORD),
    ...options,
  };
}

const CAPACITY_GPU_ONLY_BUFFER_KEYS = new Set<CapacityGpuOnlyBufferKey>([
  'pairContacts',
  'jointRecords',
  'springRecords',
  'pairActivity',
  'pairVisitedBits',
  'pairBodyContactCounts',
  'pairBodyContactIndices',
  'bodyConstraintCounts',
  'bodyConstraintRefs',
  'ignoredPairHash',
  'persistentManifoldMap',
  'pairColorBodyClaims',
  'sleepingCachedBodyRoot',
  'sleepingCachedPairKey',
  'sleepingCacheSeenBits',
  'sleepingCachePairSeenBits',
]);

function allocatedCapacity(
  value: number | undefined,
  fallback: number,
  maximum: number,
  label: string,
): number {
  const normalized = integerAtLeast(value, fallback, 0);
  const roundedPhysicalMaximum = maximum > 0
    ? nextPowerOfTwo(maximum, `${label} rounded physical maximum`)
    : 0;
  if (normalized > roundedPhysicalMaximum) {
    throw new Error(
      `${label}=${normalized} exceeds the rounded physical maximum `
      + `${roundedPhysicalMaximum} for logical maximum ${maximum}.`,
    );
  }
  return normalized;
}

function normalizeAllocationOptions(
  requested: PhysicsEngineCapacityAllocationOptions | undefined,
  input: NormalizedPhysicsEngineCapacityInput,
  maxPairContacts: number,
): NormalizedPhysicsEngineCapacityAllocationOptions {
  const mode = requested?.mode ?? 'legacy';
  if (mode !== 'legacy' && mode !== 'optimized') {
    throw new Error(`Unknown PhysicsEngine capacity mode: ${String(mode)}.`);
  }
  if (mode === 'legacy' && requested?.compactContactPoolCapacity !== undefined) {
    throw new Error('compactContactPoolCapacity requires mode="optimized".');
  }

  const compactContactPoolCapacity = mode === 'optimized'
    ? integerAtLeast(requested?.compactContactPoolCapacity, maxPairContacts, 1)
    : maxPairContacts;
  if (compactContactPoolCapacity > maxPairContacts) {
    throw new Error(
      `compactContactPoolCapacity=${compactContactPoolCapacity} exceeds the logical contact-row maximum ${maxPairContacts}.`,
    );
  }
  const contactPoolRowCapacity = compactContactPoolCapacity;
  const allocatedJoints = allocatedCapacity(
    requested?.allocatedJoints,
    input.maxJoints,
    input.maxJoints,
    'allocatedJoints',
  );
  const allocatedSprings = allocatedCapacity(
    requested?.allocatedSprings,
    input.maxSprings,
    input.maxSprings,
    'allocatedSprings',
  );
  const contactOnlyConfiguration = input.maxJoints === 0 && input.maxSprings === 0;
  const contactOnlyAdjacencyAlias = requested?.contactOnlyAdjacencyAlias
    ?? (mode === 'optimized' && contactOnlyConfiguration);
  if (contactOnlyAdjacencyAlias && !contactOnlyConfiguration) {
    throw new Error(
      'contactOnlyAdjacencyAlias requires maxJoints=0 and maxSprings=0; '
      + 'an allocated-capacity value of zero alone is not sufficient.',
    );
  }
  const includeSleepingTopologyCache = requested?.includeSleepingTopologyCache ?? false;
  if (includeSleepingTopologyCache && !contactOnlyConfiguration) {
    throw new Error(
      'includeSleepingTopologyCache requires maxJoints=0 and maxSprings=0.',
    );
  }

  const selectedGpuOnlyKeys: readonly CapacityGpuOnlyBufferKey[] = [
    ...(requested?.gpuOnlyBufferKeys
      ?? (mode === 'optimized' ? CAPACITY_PLANNER_OPTIMIZED_GPU_ONLY_BUFFER_KEYS : [])),
    ...(includeSleepingTopologyCache
      ? [
        'sleepingCachedBodyRoot',
        'sleepingCachedPairKey',
        'sleepingCacheSeenBits',
        'sleepingCachePairSeenBits',
      ] as const
      : []),
  ];
  const uniqueGpuOnlyKeys: CapacityGpuOnlyBufferKey[] = [];
  const seenGpuOnlyKeys = new Set<CapacityGpuOnlyBufferKey>();
  for (const key of selectedGpuOnlyKeys) {
    if (!CAPACITY_GPU_ONLY_BUFFER_KEYS.has(key)) {
      throw new Error(`Unsupported GPU-only capacity buffer key: ${String(key)}.`);
    }
    if (seenGpuOnlyKeys.has(key)) continue;
    seenGpuOnlyKeys.add(key);
    uniqueGpuOnlyKeys.push(key);
  }

  return Object.freeze({
    mode,
    compactContactPoolCapacity,
    contactPoolRowCapacity,
    allocatedJoints,
    allocatedSprings,
    constraintGraphUsesLogicalMax: requested?.constraintGraphUsesLogicalMax ?? false,
    contactOnlyAdjacencyAlias,
    includeSleepingTopologyCache,
    // Wide traversal owns each unordered pair by body order. The legacy dense
    // triangular ordinal is a single u32 and cannot be an exact identity for
    // >16-bit endpoint worlds, so never reserve or advertise that bitset.
    enableBroadphasePairDedup: input.pairEndpointEncoding === 'packed-u16'
      && (requested?.enableBroadphasePairDedup ?? true),
    gpuOnlyBufferKeys: Object.freeze(uniqueGpuOnlyKeys),
  });
}

function sumBufferBytes(buffers: readonly CapacityBufferDetail[], label: string): number {
  return checkedSum(label, ...buffers.map((buffer) => buffer.bytes));
}

function largestBuffer(buffers: readonly CapacityBufferDetail[]): CapacityBufferDetail {
  if (buffers.length === 0) throw new Error('Capacity plan has no buffers.');
  return buffers.reduce((largest, buffer) => buffer.bytes > largest.bytes ? buffer : largest);
}

function normalizeLimits(limits: CapacityDeviceLimits): Required<CapacityDeviceLimits> {
  const maxStorageBufferBindingSize = integerAtLeast(
    limits.maxStorageBufferBindingSize,
    0,
    0,
  );
  const maxBufferSize = integerAtLeast(limits.maxBufferSize, 0, 0);
  const maxComputeWorkgroupsPerDimension = integerAtLeast(
    limits.maxComputeWorkgroupsPerDimension,
    DEFAULT_MAX_COMPUTE_WORKGROUPS_PER_DIMENSION,
    1,
  );
  if (maxStorageBufferBindingSize === 0 || maxBufferSize === 0) {
    throw new Error('WebGPU maxStorageBufferBindingSize and maxBufferSize must both be positive.');
  }
  return {
    maxStorageBufferBindingSize,
    maxBufferSize,
    maxComputeWorkgroupsPerDimension,
  };
}

function suggestionForBuffer(
  buffer: CapacityBufferDetail,
  input: NormalizedPhysicsEngineCapacityInput,
  allocation: NormalizedPhysicsEngineCapacityAllocationOptions,
  limit: number,
): string {
  if (buffer.key === 'pairContacts') {
    if (allocation.mode === 'optimized') {
      const safeCapacity = Math.max(
        0,
        Math.floor(limit / (CONTACT_RECORD_FLOATS * BYTES_PER_WORD)),
      );
      return `Reduce compactContactPoolCapacity to <= ${safeCapacity.toLocaleString('en-US')} rows.`;
    }
    const bytesPerManifold = input.pairManifoldSlots * CONTACT_RECORD_FLOATS * BYTES_PER_WORD;
    const safeManifoldCount = Math.max(0, Math.floor(limit / bytesPerManifold));
    return `Reduce maxPersistentManifolds to <= ${safeManifoldCount.toLocaleString('en-US')}, `
      + `reduce pairManifoldSlots, or split the persistent cache from the active contact pool.`;
  }
  if (buffer.key === 'pairActivity') {
    return 'Reduce maxPairsPerBodyBroadphase/maxPersistentManifolds, or split the three compact lists into separately sized buffers.';
  }
  if (buffer.key === 'pairBodyContactIndices') {
    return 'Reduce maxContactsPerBodySolver or replace the fixed per-body contact region with a fully compact adjacency list.';
  }
  if (buffer.key === 'bodyConstraintRefs') {
    return 'Reduce maxContactsPerBodySolver/maxJoints/maxSprings, or allocate constraint endpoint storage from live counts.';
  }
  if (buffer.key === 'jointRecords') {
    return 'Reduce maxJoints or grow joint storage on demand from the live joint count.';
  }
  if (buffer.key === 'springRecords') {
    return 'Reduce maxSprings or grow spring storage on demand from the live spring count.';
  }
  if (buffer.key === 'persistentManifoldMap') {
    return 'Reduce maxPersistentManifolds or shard the persistent-manifold hash/pool into multiple storage bindings.';
  }
  return 'Lower the associated scene capacity or split this allocation into multiple storage-buffer bindings.';
}

function checkLimits(
  gpuBuffers: readonly CapacityBufferDetail[],
  input: NormalizedPhysicsEngineCapacityInput,
  allocation: NormalizedPhysicsEngineCapacityAllocationOptions,
  limits?: CapacityDeviceLimits,
): CapacityLimitStatus {
  if (!limits) {
    return {
      checked: false,
      ok: true,
      violations: [],
      warnings: [
        'Device limits were not supplied; call the planner with GPUDevice.limits before allocating GPU buffers.',
      ],
    };
  }
  const normalized = normalizeLimits(limits);
  const effectiveLimit = Math.min(
    normalized.maxStorageBufferBindingSize,
    normalized.maxBufferSize,
  );
  const violations: CapacityLimitViolation[] = [];
  for (const buffer of gpuBuffers) {
    const exceedsBinding = buffer.bytes > normalized.maxStorageBufferBindingSize;
    const exceedsBuffer = buffer.bytes > normalized.maxBufferSize;
    if (!exceedsBinding && !exceedsBuffer) continue;
    const failedLimits = [
      exceedsBinding
        ? `maxStorageBufferBindingSize=${formatByteCount(normalized.maxStorageBufferBindingSize)}`
        : '',
      exceedsBuffer ? `maxBufferSize=${formatByteCount(normalized.maxBufferSize)}` : '',
    ].filter(Boolean).join(' and ');
    violations.push({
      bufferKey: buffer.key,
      bufferLabel: buffer.label,
      bytes: buffer.bytes,
      exceedsMaxStorageBufferBindingSize: exceedsBinding,
      exceedsMaxBufferSize: exceedsBuffer,
      message: `${buffer.label} requires ${formatByteCount(buffer.bytes)}, exceeding ${failedLimits}.`,
      suggestion: suggestionForBuffer(buffer, input, allocation, effectiveLimit),
    });
  }
  const warnings: string[] = [];
  const totalGpuBytes = sumBufferBytes(gpuBuffers, 'GPU central byte total');
  if (totalGpuBytes >= 512 * 1024 * 1024) {
    warnings.push(
      `Central GPU buffers total ${formatByteCount(totalGpuBytes)}; WebGPU exposes no portable total-memory limit, so allocation can still fail despite every single buffer passing.`,
    );
  }
  return {
    checked: true,
    ok: violations.length === 0,
    maxStorageBufferBindingSize: normalized.maxStorageBufferBindingSize,
    maxBufferSize: normalized.maxBufferSize,
    effectiveStorageBufferLimit: effectiveLimit,
    violations,
    warnings,
  };
}

/**
 * Resolves the only lossless candidate endpoint ABI for a body capacity.
 * Packed mode represents body indices 0..65,535, so 65,536 bodies is the
 * inclusive legacy ceiling. The all-ones u32 endpoint stays reserved in wide
 * mode, allowing at most 0xffff_ffff bodies (indices 0..0xffff_fffe).
 */
export function resolvePhysicsPairEndpointEncoding(
  maxBodies: number,
  requested?: PairEndpointEncoding,
): PairEndpointEncoding {
  if (!Number.isSafeInteger(maxBodies) || maxBodies < 1) {
    throw new Error(`maxBodies must be a positive safe integer (received ${String(maxBodies)}).`);
  }
  if (requested !== undefined && requested !== 'packed-u16' && requested !== 'wide-u32') {
    throw new Error(`Unknown pair endpoint encoding: ${String(requested)}.`);
  }
  const encoding = requested
    ?? (maxBodies > LEGACY_PACKED_PAIR_MAX_BODY_INDEX + 1 ? 'wide-u32' : 'packed-u16');
  if (
    encoding === 'packed-u16'
    && maxBodies > LEGACY_PACKED_PAIR_MAX_BODY_INDEX + 1
  ) {
    throw new Error(
      `pairEndpointEncoding="packed-u16" supports at most `
      + `${LEGACY_PACKED_PAIR_MAX_BODY_INDEX + 1} bodies (received ${maxBodies}).`,
    );
  }
  if (encoding === 'wide-u32' && maxBodies > WIDE_PAIR_MAX_BODY_INDEX + 1) {
    throw new Error(
      `pairEndpointEncoding="wide-u32" supports at most `
      + `${WIDE_PAIR_MAX_BODY_INDEX + 1} bodies (received ${maxBodies}).`,
    );
  }
  return encoding;
}

/**
 * Produces an allocation plan without touching a GPU or allocating the large
 * typed arrays. The returned GPU list contains unique physical allocations;
 * aliases/views are recorded on their owner and are not double-counted.
 */
export function planPhysicsEngineCapacity(
  requested: PhysicsEngineCapacityInput,
  limits?: CapacityDeviceLimits,
  allocationOptions?: PhysicsEngineCapacityAllocationOptions,
): PhysicsEngineCapacityPlan {
  if (
    !Number.isFinite(requested.maxBodies)
    || !Number.isSafeInteger(requested.maxBodies)
    || requested.maxBodies < 1
  ) {
    throw new Error(`maxBodies must be a positive safe integer (received ${String(requested.maxBodies)}).`);
  }
  const maxBodies = requested.maxBodies;
  const pairEndpointEncoding = resolvePhysicsPairEndpointEncoding(
    maxBodies,
    requested.pairEndpointEncoding,
  );
  const maxPairsPerBodyBroadphase = integerAtLeast(
    requested.maxPairsPerBodyBroadphase,
    64,
    1,
  );
  const pairManifoldSlots = Math.min(
    8,
    integerAtLeast(requested.pairManifoldSlots, 4, 1),
  );
  const maxContactsPerBodySolver = integerAtLeast(
    requested.maxContactsPerBodySolver,
    128,
    1,
  );
  const maxPairs = checkedProduct('maxPairs numerator', maxBodies, maxBodies - 1) / 2;
  const maxCandidatePairs = checkedProduct(
    'maxCandidatePairs',
    maxBodies,
    maxPairsPerBodyBroadphase,
  );
  const requestedPersistentManifolds = integerAtLeast(
    requested.maxPersistentManifolds,
    maxCandidatePairs,
    0,
  );
  const maxPersistentManifolds = Math.max(
    1,
    Math.min(maxCandidatePairs, requestedPersistentManifolds),
  );
  const maxJoints = integerAtLeast(
    requested.maxJoints,
    checkedProduct('default maxJoints', maxBodies, DEFAULT_MAX_JOINTS_PER_BODY_SOLVER),
    0,
  );
  const maxSprings = integerAtLeast(
    requested.maxSprings,
    checkedProduct('default maxSprings', maxBodies, DEFAULT_MAX_SPRINGS_PER_BODY_SOLVER),
    0,
  );
  const requestedIgnoredPairs = integerAtLeast(
    requested.maxIgnoredPairs,
    checkedSum('default maxIgnoredPairs', maxJoints, maxSprings),
    0,
  );
  const maxIgnoredPairs = Math.min(maxPairs, requestedIgnoredPairs);
  const input: NormalizedPhysicsEngineCapacityInput = {
    maxBodies,
    pairEndpointEncoding,
    maxPairsPerBodyBroadphase,
    maxPersistentManifolds,
    pairManifoldSlots,
    maxContactsPerBodySolver,
    maxJoints,
    maxSprings,
    maxIgnoredPairs,
  };

  const maxPairContacts = checkedProduct(
    'maxPairContacts',
    maxPersistentManifolds,
    pairManifoldSlots,
  );
  const allocation = normalizeAllocationOptions(allocationOptions, input, maxPairContacts);
  const maxActivePairContacts = allocation.compactContactPoolCapacity;
  const graphJointCapacity = allocation.constraintGraphUsesLogicalMax
    ? input.maxJoints
    : allocation.allocatedJoints;
  const graphSpringCapacity = allocation.constraintGraphUsesLogicalMax
    ? input.maxSprings
    : allocation.allocatedSprings;
  const maxJointsPerBodySolver = graphJointCapacity > 0
    ? Math.min(DEFAULT_MAX_JOINTS_PER_BODY_SOLVER, graphJointCapacity)
    : 0;
  const maxSpringsPerBodySolver = graphSpringCapacity > 0
    ? Math.min(DEFAULT_MAX_SPRINGS_PER_BODY_SOLVER, graphSpringCapacity)
    : 0;
  const maxConstraintsPerBodySolver = checkedSum(
    'maxConstraintsPerBodySolver',
    maxContactsPerBodySolver,
    maxJointsPerBodySolver,
    maxSpringsPerBodySolver,
  );
  const maxBodyContactOverflowNodes = Math.max(
    1,
    Math.min(
      checkedProduct('contact endpoint count', maxActivePairContacts, 2),
      checkedProduct('contact spill budget', maxBodies, 16),
    ),
  );
  const maxBodyConstraintOverflowNodes = Math.max(
    1,
    checkedSum(
      'maxBodyConstraintOverflowNodes',
      maxBodyContactOverflowNodes,
      checkedProduct(
        'joint/spring endpoint count',
        checkedSum(
          'allocated joint/spring count',
          graphJointCapacity,
          graphSpringCapacity,
        ),
        2,
      ),
    ),
  );
  const persistentManifoldHashCapacity = nextPowerOfTwo(
    Math.max(2, checkedProduct('persistent hash target', maxPersistentManifolds, 2)),
    'Persistent-manifold hash capacity',
  );
  const ignoredPairHashCapacity = nextPowerOfTwo(
    Math.max(1, checkedProduct('ignored-pair hash target', maxIgnoredPairs, 2)),
    'Ignored-pair hash capacity',
  );
  const storageMaxJoints = Math.max(1, allocation.allocatedJoints);
  const storageMaxSprings = Math.max(1, allocation.allocatedSprings);
  const pairActivityLayout = planPairActivityLayout(
    maxCandidatePairs,
    maxActivePairContacts,
    input.pairEndpointEncoding,
  );
  const pairActivityWordCount = pairActivityLayout.totalWords;
  const workgroupLimit = limits
    ? normalizeLimits(limits).maxComputeWorkgroupsPerDimension
    : DEFAULT_MAX_COMPUTE_WORKGROUPS_PER_DIMENSION;
  const pairVisitedWordCount = allocation.enableBroadphasePairDedup
    ? Math.min(
      Math.ceil(maxPairs / 32),
      checkedProduct('pair visited dispatch word limit', workgroupLimit, CANDIDATE_WORKGROUP_SIZE),
    )
    // Broadphase keeps binding 5 present even when the shader is forbidden
    // from touching the visited set. One word is the minimum valid binding.
    : 1;
  const pairBodyContactIndicesWordCount = checkedSum(
    'pairBodyContactIndicesWordCount',
    checkedProduct('pair-body fixed words', maxBodies, maxContactsPerBodySolver),
    1,
    maxBodies,
    checkedProduct('pair-body overflow words', maxBodyContactOverflowNodes, 2),
  );
  const independentBodyConstraintRefsWordCount = checkedSum(
    'bodyConstraintRefsWordCount',
    checkedProduct('body-constraint fixed words', maxBodies, maxConstraintsPerBodySolver),
    1,
    maxBodies,
    checkedProduct('body-constraint overflow words', maxBodyConstraintOverflowNodes, 2),
  );
  const bodyConstraintRefsWordCount = allocation.contactOnlyAdjacencyAlias
    ? pairBodyContactIndicesWordCount
    : independentBodyConstraintRefsWordCount;
  const persistentManifoldPoolArrayCount = allocation.mode === 'optimized'
    ? COMPACT_PERSISTENT_MANIFOLD_LIFECYCLE_ARRAY_COUNT
    : LEGACY_PERSISTENT_MANIFOLD_POOL_ARRAY_COUNT;
  const persistentManifoldMapWordCount = allocation.mode === 'optimized'
    ? planCompactContactPoolLayout({
      persistentManifoldHashCapacity,
      maxPersistentManifolds,
      pairManifoldSlots,
      compactContactPoolCapacity: allocation.compactContactPoolCapacity,
    }).totalWords
    : checkedSum(
      'persistentManifoldMapWordCount',
      PERSISTENT_MANIFOLD_HEADER_WORDS,
      checkedProduct('persistent hash words', persistentManifoldHashCapacity, 2),
      checkedProduct(
        'persistent pool lifecycle words',
        maxPersistentManifolds,
        persistentManifoldPoolArrayCount,
      ),
    );
  const sleepingCachePairHashCapacity = allocation.includeSleepingTopologyCache
    ? nextPowerOfTwo(
      checkedProduct('sleeping canonical-pair hash target', maxPersistentManifolds, 2),
      'Sleeping canonical-pair hash capacity',
    )
    : 0;
  const sleepingCacheRowSeenWordCount = allocation.includeSleepingTopologyCache
    ? Math.max(1, Math.ceil(allocation.contactPoolRowCapacity / 32))
    : 0;
  const sleepingCachePairSeenWordCount = allocation.includeSleepingTopologyCache
    ? Math.max(1, Math.ceil(sleepingCachePairHashCapacity / 32))
    : 0;
  const sleepingCacheClearWorkgroups = allocation.includeSleepingTopologyCache
    ? Math.ceil(Math.max(
      sleepingCachePairHashCapacity,
      sleepingCacheRowSeenWordCount,
      sleepingCachePairSeenWordCount,
    ) / 256)
    : 0;
  if (sleepingCacheClearWorkgroups > workgroupLimit) {
    throw new Error(
      `Sleeping topology-cache clear requires ${sleepingCacheClearWorkgroups} workgroups, `
      + `exceeding maxComputeWorkgroupsPerDimension=${workgroupLimit}.`,
    );
  }
  const sleepingCacheSeenWordCount = checkedSum(
    'sleepingCacheSeenWordCount',
    sleepingCacheRowSeenWordCount,
    sleepingCachePairSeenWordCount,
  );
  const derived: PhysicsEngineCapacityDerived = {
    maxPairs,
    maxCandidatePairs,
    maxPairContacts,
    maxActivePairContacts,
    compactContactPoolCapacity: allocation.compactContactPoolCapacity,
    contactPoolRowCapacity: allocation.contactPoolRowCapacity,
    persistentManifoldHashCapacity,
    persistentManifoldPoolArrayCount,
    ignoredPairHashCapacity,
    maxJointsPerBodySolver,
    maxSpringsPerBodySolver,
    maxConstraintsPerBodySolver,
    maxBodyContactOverflowNodes,
    maxBodyConstraintOverflowNodes,
    storageMaxJoints,
    storageMaxSprings,
    allocatedJoints: allocation.allocatedJoints,
    allocatedSprings: allocation.allocatedSprings,
    contactOnlyAdjacencyAliased: allocation.contactOnlyAdjacencyAlias,
    pairActivityWordCount,
    pairActivityLayout,
    pairVisitedWordCount,
    pairBodyContactIndicesWordCount,
    bodyConstraintRefsWordCount,
    persistentManifoldMapWordCount,
    sleepingCachePairHashCapacity,
    sleepingCacheRowSeenWordCount,
    sleepingCachePairSeenWordCount,
    sleepingCacheSeenWordCount,
  };

  const pairBodyContactCpuAliases = allocation.contactOnlyAdjacencyAlias
    ? ['bodyConstraintRefsData']
    : undefined;
  const pairBodyContactGpuAliases = allocation.contactOnlyAdjacencyAlias
    ? ['bodyConstraintRefsAttr']
    : undefined;
  const fullCpuBuffers: CapacityBufferDetail[] = [
    makeBuffer('cpu', 'positions', 'positionsData', 'float32', maxBodies * 4),
    makeBuffer('cpu', 'initialPose', 'initialPoseData', 'float32', maxBodies * 8),
    makeBuffer('cpu', 'initialLinearVel', 'initialLinearVelData', 'float32', maxBodies * 4, { cpuOnly: true }),
    makeBuffer('cpu', 'initialAngularVel', 'initialAngularVelData', 'float32', maxBodies * 4, { cpuOnly: true }),
    makeBuffer('cpu', 'inertialPose', 'inertialPoseData', 'float32', maxBodies * INERTIAL_POSE_FLOATS_PER_BODY),
    makeBuffer('cpu', 'velocities', 'velocitiesData', 'float32', maxBodies * 4),
    makeBuffer('cpu', 'prevLinearVel', 'prevLinearVelData', 'float32', maxBodies * 4),
    makeBuffer('cpu', 'shapes', 'shapesData', 'float32', maxBodies * 4, { aliases: ['shapesWordData'] }),
    makeBuffer('cpu', 'quaternions', 'quaternionsData', 'float32', maxBodies * 4),
    makeBuffer('cpu', 'angularVel', 'angularVelData', 'float32', maxBodies * 4),
    makeBuffer('cpu', 'inverseInertia', 'inverseInertiaData', 'float32', maxBodies * 4),
    makeBuffer('cpu', 'conservativeBodyFlags', 'conservativeBodyFlagsData', 'uint32', maxBodies),
    makeBuffer(
      'cpu',
      'pairContacts',
      'pairContactsData',
      'float32',
      allocation.contactPoolRowCapacity * CONTACT_RECORD_FLOATS,
    ),
    makeBuffer(
      'cpu',
      'jointRecords',
      'jointRecordsData',
      'float32',
      storageMaxJoints * JOINT_RECORD_FLOATS,
    ),
    makeBuffer(
      'cpu',
      'springRecords',
      'springRecordsData',
      'float32',
      storageMaxSprings * SPRING_RECORD_FLOATS,
    ),
    makeBuffer('cpu', 'pairActivity', 'pairActivityData', 'uint32', pairActivityWordCount, {
      aliases: [
        'pairCandidateIndicesData',
        'pairActiveCandidateSlotsData',
        'pairActiveContactsData',
      ],
    }),
    makeBuffer('cpu', 'pairVisitedBits', 'pairVisitedBitsData', 'uint32', pairVisitedWordCount),
    makeBuffer('cpu', 'pairBodyContactCounts', 'pairBodyContactCountsData', 'uint32', maxBodies),
    makeBuffer(
      'cpu',
      'pairBodyContactIndices',
      'pairBodyContactIndicesData',
      'uint32',
      pairBodyContactIndicesWordCount,
      { aliases: pairBodyContactCpuAliases },
    ),
    makeBuffer('cpu', 'bodyConstraintCounts', 'bodyConstraintCountsData', 'uint32', maxBodies),
    ...(
      allocation.contactOnlyAdjacencyAlias
        ? []
        : [makeBuffer(
          'cpu',
          'bodyConstraintRefs',
          'bodyConstraintRefsData',
          'uint32',
          bodyConstraintRefsWordCount,
        )]
    ),
    makeBuffer(
      'cpu',
      'ignoredPairHash',
      'ignoredPairHashData',
      'uint32',
      ignoredPairHashCapacity * IGNORED_PAIR_HASH_ENTRY_WORDS,
    ),
    makeBuffer(
      'cpu',
      'persistentManifoldMap',
      'persistentManifoldMapData',
      'uint32',
      persistentManifoldMapWordCount,
    ),
    makeBuffer('cpu', 'pairColorBodyClaims', 'pairColorBodyClaimsData', 'uint32', maxBodies),
    ...(allocation.includeSleepingTopologyCache
      ? [
        makeBuffer(
          'cpu',
          'sleepingCachedBodyRoot',
          'sleepingCachedBodyRootData',
          'uint32',
          maxBodies,
        ),
        makeBuffer(
          'cpu',
          'sleepingCachedPairKey',
          'sleepingCachedPairKeyData',
          'uint32',
          sleepingCachePairHashCapacity,
        ),
        makeBuffer(
          'cpu',
          'sleepingCacheSeenBits',
          'sleepingCacheSeenBitsData',
          'uint32',
          sleepingCacheRowSeenWordCount,
        ),
        makeBuffer(
          'cpu',
          'sleepingCachePairSeenBits',
          'sleepingCachePairSeenBitsData',
          'uint32',
          sleepingCachePairSeenWordCount,
        ),
      ]
      : []),
  ];

  const gpuBuffers = fullCpuBuffers
    .filter((buffer) => !buffer.cpuOnly)
    .map((buffer): CapacityBufferDetail => ({
      ...buffer,
      domain: 'gpu',
      label: `${buffer.key}Attr`,
      cpuOnly: undefined,
      aliases: buffer.key === 'pairActivity'
        ? ['pairCandidateIndicesAttr', 'pairActiveCandidateSlotsAttr', 'pairActiveContactsAttr']
        : buffer.key === 'pairBodyContactIndices'
          ? pairBodyContactGpuAliases
          : undefined,
    }));
  const gpuOnlyKeys = new Set<CapacityGpuOnlyBufferKey>(allocation.gpuOnlyBufferKeys);
  const cpuBuffers = fullCpuBuffers.map((buffer): CapacityBufferDetail => {
    if (buffer.cpuOnly || !gpuOnlyKeys.has(buffer.key as CapacityGpuOnlyBufferKey)) {
      return buffer;
    }
    // All float storage records in this planner are bound as vec4 arrays;
    // uint storage is scalar. This exactly matches gpuOnlyStorage's one-item
    // placeholder rather than charging the full logical GPU allocation twice.
    const placeholderElementCount = buffer.elementType === 'float32' ? 4 : 1;
    return makeBuffer(
      'cpu',
      buffer.key,
      buffer.label,
      buffer.elementType,
      placeholderElementCount,
      {
        aliases: buffer.aliases,
        logicalElementCount: buffer.elementCount,
        gpuOnlyPlaceholder: true,
      },
    );
  });
  const totalCpuBytes = sumBufferBytes(cpuBuffers, 'CPU central byte total');
  const totalGpuBytes = sumBufferBytes(gpuBuffers, 'GPU central byte total');

  return {
    input,
    allocation,
    derived,
    cpuBuffers,
    gpuBuffers,
    totalCpuBytes,
    totalGpuBytes,
    totalCentralBytes: checkedSum('combined CPU/GPU byte total', totalCpuBytes, totalGpuBytes),
    largestCpuBuffer: largestBuffer(cpuBuffers),
    largestGpuBuffer: largestBuffer(gpuBuffers),
    limitStatus: checkLimits(gpuBuffers, input, allocation, limits),
  };
}

export function formatByteCount(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return String(bytes);
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let unit = 0;
  let value = bytes;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = unit === 0 ? 0 : value >= 100 ? 1 : 2;
  return `${value.toFixed(digits)} ${units[unit]} (${bytes.toLocaleString('en-US')} B)`;
}

/** Human-readable report suitable for the diagnostics UI or a thrown error. */
export function formatPhysicsEngineCapacityReport(plan: PhysicsEngineCapacityPlan): string {
  const lines: string[] = [
    `PhysicsEngine central capacity (${plan.input.maxBodies.toLocaleString('en-US')} bodies)`,
    `Dry-run allocation model (must match installed engine storage): ${plan.allocation.mode}`,
    plan.allocation.mode === 'optimized'
      ? `Single compact contact pool: ${plan.allocation.compactContactPoolCapacity.toLocaleString('en-US')} rows`
      : `Fixed-slot contact pool: ${plan.allocation.contactPoolRowCapacity.toLocaleString('en-US')} rows`,
    `CPU: ${formatByteCount(plan.totalCpuBytes)}`,
    `GPU: ${formatByteCount(plan.totalGpuBytes)}`,
    `CPU + GPU backing: ${formatByteCount(plan.totalCentralBytes)}`,
    `Largest GPU buffer: ${plan.largestGpuBuffer.label} = ${formatByteCount(plan.largestGpuBuffer.bytes)}`,
    '',
    'CPU allocations:',
  ];
  for (const buffer of [...plan.cpuBuffers].sort((a, b) => b.bytes - a.bytes)) {
    const annotations = [
      buffer.cpuOnly ? 'CPU only' : '',
      buffer.gpuOnlyPlaceholder
        ? `GPU-only placeholder for ${buffer.logicalElementCount?.toLocaleString('en-US')} elements`
        : '',
      buffer.aliases?.length ? `aliases: ${buffer.aliases.join(', ')}` : '',
    ].filter(Boolean);
    lines.push(
      `  ${buffer.label}: ${formatByteCount(buffer.bytes)} (${buffer.elementCount.toLocaleString('en-US')} ${buffer.elementType})${annotations.length ? ` [${annotations.join('; ')}]` : ''}`,
    );
  }
  lines.push(
    '',
    'GPU storage buffers:',
  );
  for (const buffer of [...plan.gpuBuffers].sort((a, b) => b.bytes - a.bytes)) {
    const aliases = buffer.aliases?.length ? ` [aliases: ${buffer.aliases.join(', ')}]` : '';
    lines.push(`  ${buffer.label}: ${formatByteCount(buffer.bytes)} (${buffer.elementCount.toLocaleString('en-US')} ${buffer.elementType})${aliases}`);
  }
  if (!plan.limitStatus.checked) {
    lines.push('', `Limits: not checked. ${plan.limitStatus.warnings.join(' ')}`);
  } else if (plan.limitStatus.ok) {
    lines.push(
      '',
      `Limits: PASS (binding ${formatByteCount(plan.limitStatus.maxStorageBufferBindingSize!)}, buffer ${formatByteCount(plan.limitStatus.maxBufferSize!)}).`,
    );
  } else {
    lines.push('', 'Limits: FAIL');
    for (const violation of plan.limitStatus.violations) {
      lines.push(`  ERROR: ${violation.message}`);
      lines.push(`  SUGGESTION: ${violation.suggestion}`);
    }
  }
  for (const warning of plan.limitStatus.warnings) lines.push(`WARNING: ${warning}`);
  return lines.join('\n');
}

/**
 * Constructor-friendly guard: performs the same dry-run and throws before any
 * large typed array or GPUBuffer is allocated. The exception includes the
 * precise offending buffers and actionable capacity reductions.
 */
export function assertPhysicsEngineCapacityFitsDevice(
  requested: PhysicsEngineCapacityInput,
  limits: CapacityDeviceLimits,
  allocationOptions?: PhysicsEngineCapacityAllocationOptions,
): PhysicsEngineCapacityPlan {
  const plan = planPhysicsEngineCapacity(requested, limits, allocationOptions);
  if (!plan.limitStatus.ok) {
    throw new Error(`PhysicsEngine capacity is not supported by this WebGPU device.\n${formatPhysicsEngineCapacityReport(plan)}`);
  }
  return plan;
}

export const CAPACITY_PLANNER_REFERENCE_PROFILES = {
  coliseum50k: {
    maxBodies: 50_688,
    maxPairsPerBodyBroadphase: 16,
    maxPersistentManifolds: 196_608,
    pairManifoldSlots: 8,
    maxContactsPerBodySolver: 64,
    maxJoints: 0,
    maxSprings: 0,
    maxIgnoredPairs: 0,
  },
  coliseum64k: {
    maxBodies: 64_512,
    maxPairsPerBodyBroadphase: 16,
    maxPersistentManifolds: 262_144,
    pairManifoldSlots: 8,
    maxContactsPerBodySolver: 64,
    maxJoints: 0,
    maxSprings: 0,
    maxIgnoredPairs: 0,
  },
} as const satisfies Record<string, PhysicsEngineCapacityInput>;

/**
 * GPU-free deterministic assertions for both allocation models. Every
 * production-profile byte total is literal so formula drift cannot make this
 * check tautological.
 */
export function runCapacityPlannerSelfTests(): CapacityPlannerSelfTestResult {
  const expected = {
    coliseum50k: {
      legacy: {
        totalCpuBytes: 316_510_236,
        totalGpuBytes: 314_888_220,
        totalCentralBytes: 631_398_456,
        largestGpuBufferBytes: 226_492_416,
        contactPoolRowCapacity: 1_572_864,
      },
      optimized: {
        totalCpuBytes: 13_179_172,
        totalGpuBytes: 192_198_784,
        totalCentralBytes: 205_377_956,
        largestGpuBufferBytes: 113_246_208,
        contactPoolRowCapacity: 786_432,
      },
    },
    coliseum64k: {
      legacy: {
        totalCpuBytes: 411_410_460,
        totalGpuBytes: 409_346_076,
        totalCentralBytes: 820_756_536,
        largestGpuBufferBytes: 301_989_888,
        contactPoolRowCapacity: 2_097_152,
      },
      optimized: {
        totalCpuBytes: 16_773_412,
        totalGpuBytes: 226_504_832,
        totalCentralBytes: 243_278_244,
        largestGpuBufferBytes: 132_120_576,
        contactPoolRowCapacity: 917_504,
      },
    },
  } as const;
  const failures: string[] = [];
  const profiles: CapacityPlannerSelfTestResult['profiles'] = [];
  const optimizedPoolCapacity = {
    coliseum50k: 786_432,
    coliseum64k: 917_504,
  } as const;
  const check = (name: string, condition: boolean): void => {
    if (!condition) failures.push(name);
  };

  for (const [name, input] of Object.entries(CAPACITY_PLANNER_REFERENCE_PROFILES)) {
    for (const mode of ['legacy', 'optimized'] as const) {
      const allocationOptions: PhysicsEngineCapacityAllocationOptions = mode === 'legacy'
        ? { mode }
        : { mode, compactContactPoolCapacity: optimizedPoolCapacity[name as keyof typeof optimizedPoolCapacity] };
      const plan = planPhysicsEngineCapacity(input, undefined, allocationOptions);
      const wanted = expected[name as keyof typeof expected][mode];
      const actual = {
        totalCpuBytes: plan.totalCpuBytes,
        totalGpuBytes: plan.totalGpuBytes,
        totalCentralBytes: plan.totalCentralBytes,
        largestGpuBufferBytes: plan.largestGpuBuffer.bytes,
        contactPoolRowCapacity: plan.derived.contactPoolRowCapacity,
      };
      profiles.push({ name, mode, ...actual });
      for (const key of Object.keys(wanted) as Array<keyof typeof wanted>) {
        if (actual[key] !== wanted[key]) {
          failures.push(
            `${name}.${mode}.${key}: expected ${wanted[key]}, received ${actual[key]}`,
          );
        }
      }
      check(
        `${name}.${mode}: duplicate physical GPU allocation key`,
        new Set(plan.gpuBuffers.map((buffer) => buffer.key)).size === plan.gpuBuffers.length,
      );
      check(
        `${name}.${mode}: wrong persistent lifecycle array count`,
        plan.derived.persistentManifoldPoolArrayCount === (mode === 'legacy' ? 4 : 3),
      );
    }
  }

  const compactSelfTest = runCompactContactPoolSelfTests();
  for (const failure of compactSelfTest.failures) failures.push(`compact pool: ${failure}`);
  const wideCompactSelfTest = runWideCompactContactPoolSelfTests();
  for (const failure of wideCompactSelfTest.failures) {
    failures.push(`wide compact pool: ${failure}`);
  }
  const wideContactGenerationSelfTest = runWideExactContactGenerationSelfTests();
  for (const failure of wideContactGenerationSelfTest.failures) {
    failures.push(`wide contact generation: ${failure}`);
  }
  const contactOnlyCsrSelfTest = runContactOnlyCsrSelfTests();
  for (const failure of contactOnlyCsrSelfTest.failures) {
    failures.push(`contact-only CSR: ${failure}`);
  }
  const contactOnlyCsrGpuSelfTest = runContactOnlyCsrGpuSelfTests();
  for (const failure of contactOnlyCsrGpuSelfTest.failures) {
    failures.push(`contact-only CSR GPU: ${failure}`);
  }
  const contactOnlyCsrGpuRuntimeSelfTest = runContactOnlyCsrGpuRuntimeSelfTests();
  for (const failure of contactOnlyCsrGpuRuntimeSelfTest.failures) {
    failures.push(`contact-only CSR GPU runtime: ${failure}`);
  }
  const canonicalContactStorageSelfTest = runCanonicalContactStorageLayoutSelfTests();
  for (const failure of canonicalContactStorageSelfTest.failures) {
    failures.push(`canonical contact SoA: ${failure}`);
  }
  const pairAbiSelfTest = runPairAbiSelfTests();
  for (const failure of pairAbiSelfTest.failures) failures.push(`pair ABI: ${failure}`);
  check('pair ABI self-test did not execute every phase-one invariant',
    pairAbiSelfTest.ok && pairAbiSelfTest.checks === 19);
  check(
    'pair endpoint auto-selection changed at the packed boundary',
    resolvePhysicsPairEndpointEncoding(65_536) === 'packed-u16'
      && resolvePhysicsPairEndpointEncoding(65_537) === 'wide-u32',
  );
  const packed64kPairActivity = planPhysicsEngineCapacity(
    {
      maxBodies: 65_536,
      maxPairsPerBodyBroadphase: 1,
      maxPersistentManifolds: 1,
      pairManifoldSlots: 1,
      maxContactsPerBodySolver: 1,
      maxJoints: 0,
      maxSprings: 0,
      maxIgnoredPairs: 0,
    },
    undefined,
    {
      mode: 'optimized',
      compactContactPoolCapacity: 1,
      enableBroadphasePairDedup: false,
    },
  );
  const packed64kLayout = packed64kPairActivity.derived.pairActivityLayout;
  check(
    '64K planner changed the established packed candidate layout',
    packed64kPairActivity.input.pairEndpointEncoding === 'packed-u16'
      && packed64kLayout.encoding === 'packed-u16'
      && packed64kLayout.candidatePackedPairs?.offset === 1
      && packed64kLayout.candidatePackedPairs.length === 65_536
      && packed64kLayout.candidateBodyA === null
      && packed64kLayout.candidateBodyB === null
      && packed64kLayout.activeCandidateCountOffset === 65_537,
  );
  let rejectedOversizedPackedEndpoints = false;
  try {
    resolvePhysicsPairEndpointEncoding(65_537, 'packed-u16');
  } catch (error) {
    rejectedOversizedPackedEndpoints = error instanceof Error
      && error.message.includes('supports at most 65536 bodies');
  }
  check(
    'explicit packed endpoints must reject the first aliased body capacity',
    rejectedOversizedPackedEndpoints,
  );
  const wide510kPairActivity = planPhysicsEngineCapacity(
    {
      maxBodies: 510_000,
      maxPairsPerBodyBroadphase: 16,
      maxPersistentManifolds: 2_097_152,
      pairManifoldSlots: 8,
      maxContactsPerBodySolver: 64,
      maxJoints: 0,
      maxSprings: 0,
      maxIgnoredPairs: 0,
    },
    undefined,
    {
      mode: 'optimized',
      compactContactPoolCapacity: 7_340_032,
      // The normalized wide policy must still disable this inexact bitset.
      enableBroadphasePairDedup: true,
    },
  );
  const wide510kLayout = wide510kPairActivity.derived.pairActivityLayout;
  check(
    '510K planner did not select the exact endpoint ABI',
    wide510kPairActivity.input.pairEndpointEncoding === 'wide-u32'
      && wide510kLayout.encoding === 'wide-u32'
      && wide510kLayout.candidateBodyA?.offset === 1
      && wide510kLayout.candidateBodyB?.offset === 8_160_001
      && wide510kLayout.activeCandidateCountOffset === 16_320_001
      && wide510kLayout.activeContactCountOffset === 24_480_002
      && wide510kLayout.totalWords === 31_820_035
      && wide510kLayout.totalBytes === 127_280_140,
  );
  check(
    '510K planner retained the inexact triangular pair-visited allocation',
    !wide510kPairActivity.allocation.enableBroadphasePairDedup
      && wide510kPairActivity.derived.pairVisitedWordCount === 1,
  );
  const defaultOptimized = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum50k,
    undefined,
    { mode: 'optimized' },
  );
  check(
    'omitted compactContactPoolCapacity must conservatively preserve legacy row capacity',
    defaultOptimized.allocation.compactContactPoolCapacity === defaultOptimized.derived.maxPairContacts
      && defaultOptimized.derived.contactPoolRowCapacity === defaultOptimized.derived.maxPairContacts,
  );

  // PhysicsEngine intentionally keeps pairVisited mirrored so reset can clear
  // both CPU and renderer-owned storage. On the 50K/64K production profiles,
  // however, the all-pairs bitset cannot fit a single dispatch and therefore
  // can never be read. Certify the opt-out as an exact one-word allocation in
  // both domains while preserving the established auto-enabled default.
  const runtimeGpuOnlyKeys = Object.freeze([
    'pairContacts',
    'jointRecords',
    'springRecords',
    'persistentManifoldMap',
  ] as const satisfies readonly CapacityGpuOnlyBufferKey[]);
  for (const [name, input] of Object.entries(CAPACITY_PLANNER_REFERENCE_PROFILES)) {
    const compactContactPoolCapacity = optimizedPoolCapacity[
      name as keyof typeof optimizedPoolCapacity
    ];
    const commonRuntimeOptions = {
      mode: 'optimized',
      compactContactPoolCapacity,
      allocatedJoints: 0,
      allocatedSprings: 0,
      constraintGraphUsesLogicalMax: true,
      contactOnlyAdjacencyAlias: true,
      includeSleepingTopologyCache: true,
      gpuOnlyBufferKeys: runtimeGpuOnlyKeys,
    } as const satisfies PhysicsEngineCapacityAllocationOptions;
    const defaultPairDedup = planPhysicsEngineCapacity(
      input,
      undefined,
      commonRuntimeOptions,
    );
    const disabledPairDedup = planPhysicsEngineCapacity(
      input,
      undefined,
      { ...commonRuntimeOptions, enableBroadphasePairDedup: false },
    );
    const defaultCpuVisited = defaultPairDedup.cpuBuffers.find(
      (buffer) => buffer.key === 'pairVisitedBits',
    );
    const defaultGpuVisited = defaultPairDedup.gpuBuffers.find(
      (buffer) => buffer.key === 'pairVisitedBits',
    );
    const disabledCpuVisited = disabledPairDedup.cpuBuffers.find(
      (buffer) => buffer.key === 'pairVisitedBits',
    );
    const disabledGpuVisited = disabledPairDedup.gpuBuffers.find(
      (buffer) => buffer.key === 'pairVisitedBits',
    );
    const fullVisitedWords = DEFAULT_MAX_COMPUTE_WORKGROUPS_PER_DIMENSION
      * CANDIDATE_WORKGROUP_SIZE;
    const fullVisitedBytes = fullVisitedWords * BYTES_PER_WORD;
    const savedBytesPerDomain = fullVisitedBytes - BYTES_PER_WORD;
    check(
      `${name}: pair dedup default allocation changed`,
      defaultPairDedup.allocation.enableBroadphasePairDedup
        && defaultPairDedup.derived.pairVisitedWordCount === fullVisitedWords
        && defaultCpuVisited?.bytes === fullVisitedBytes
        && defaultGpuVisited?.bytes === fullVisitedBytes,
    );
    check(
      `${name}: disabled pair dedup must retain exactly one sentinel word`,
      !disabledPairDedup.allocation.enableBroadphasePairDedup
        && disabledPairDedup.derived.pairVisitedWordCount === 1
        && disabledCpuVisited?.bytes === BYTES_PER_WORD
        && disabledGpuVisited?.bytes === BYTES_PER_WORD,
    );
    check(
      `${name}: disabled pair dedup CPU/GPU savings changed`,
      defaultPairDedup.totalCpuBytes - disabledPairDedup.totalCpuBytes
        === savedBytesPerDomain
        && defaultPairDedup.totalGpuBytes - disabledPairDedup.totalGpuBytes
          === savedBytesPerDomain,
    );
  }

  const constrainedLimits: CapacityDeviceLimits = {
    maxStorageBufferBindingSize: 128 * 1024 * 1024,
    maxBufferSize: 256 * 1024 * 1024,
    maxComputeWorkgroupsPerDimension: 65_535,
  };
  const optimized50kAllocation = {
    mode: 'optimized',
    compactContactPoolCapacity: optimizedPoolCapacity.coliseum50k,
  } as const satisfies PhysicsEngineCapacityAllocationOptions;
  const optimized64kAllocation = {
    mode: 'optimized',
    compactContactPoolCapacity: optimizedPoolCapacity.coliseum64k,
  } as const satisfies PhysicsEngineCapacityAllocationOptions;
  const legacy50k = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum50k,
    constrainedLimits,
    { mode: 'legacy' },
  );
  const legacy64k = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum64k,
    constrainedLimits,
    { mode: 'legacy' },
  );
  const optimized50k = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum50k,
    constrainedLimits,
    optimized50kAllocation,
  );
  const optimized64k = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum64k,
    constrainedLimits,
    optimized64kAllocation,
  );
  const legacy50kViolation = legacy50k.limitStatus.violations[0];
  const legacy64kViolation = legacy64k.limitStatus.violations[0];
  check(
    'legacy coliseum50k synthetic device-limit classification changed',
    !legacy50k.limitStatus.ok
      && legacy50k.limitStatus.violations.length === 1
      && legacy50kViolation?.bufferKey === 'pairContacts'
      && legacy50kViolation.exceedsMaxStorageBufferBindingSize
      && !legacy50kViolation.exceedsMaxBufferSize,
  );
  check(
    'legacy coliseum64k synthetic device-limit classification changed',
    !legacy64k.limitStatus.ok
      && legacy64k.limitStatus.violations.length === 1
      && legacy64kViolation?.bufferKey === 'pairContacts'
      && legacy64kViolation.exceedsMaxStorageBufferBindingSize
      && legacy64kViolation.exceedsMaxBufferSize,
  );
  check('optimized coliseum50k should fit the synthetic device', optimized50k.limitStatus.ok);
  check('optimized coliseum64k should fit the synthetic device', optimized64k.limitStatus.ok);

  let sleepingClearLimitRejected = false;
  try {
    planPhysicsEngineCapacity(
      {
        maxBodies: 32,
        maxPairsPerBodyBroadphase: 8,
        maxPersistentManifolds: 1024,
        pairManifoldSlots: 4,
        maxContactsPerBodySolver: 16,
        maxJoints: 0,
        maxSprings: 0,
        maxIgnoredPairs: 0,
      },
      {
        maxStorageBufferBindingSize: 128 * 1024 * 1024,
        maxBufferSize: 256 * 1024 * 1024,
        maxComputeWorkgroupsPerDimension: 1,
      },
      {
        mode: 'optimized',
        compactContactPoolCapacity: 128,
        contactOnlyAdjacencyAlias: true,
        includeSleepingTopologyCache: true,
      },
    );
  } catch (error) {
    sleepingClearLimitRejected = error instanceof Error
      && error.message.includes('Sleeping topology-cache clear requires')
      && error.message.includes('maxComputeWorkgroupsPerDimension=1');
  }
  check(
    'sleeping topology cache must reject a clear dispatch beyond the device dimension',
    sleepingClearLimitRejected,
  );

  // The compact pool is one physical buffer. Test its
  // exact inclusive boundary independently for each WebGPU single-buffer cap.
  const optimized64kPoolBytes = expected.coliseum64k.optimized.largestGpuBufferBytes;
  const exactPoolLimits: CapacityDeviceLimits = {
    maxStorageBufferBindingSize: optimized64kPoolBytes,
    maxBufferSize: optimized64kPoolBytes,
  };
  const bindingOnlyLimits: CapacityDeviceLimits = {
    maxStorageBufferBindingSize: optimized64kPoolBytes - BYTES_PER_WORD,
    maxBufferSize: optimized64kPoolBytes,
  };
  const bufferOnlyLimits: CapacityDeviceLimits = {
    maxStorageBufferBindingSize: optimized64kPoolBytes,
    maxBufferSize: optimized64kPoolBytes - BYTES_PER_WORD,
  };
  const exactPoolPlan = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum64k,
    exactPoolLimits,
    optimized64kAllocation,
  );
  const bindingOnlyPlan = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum64k,
    bindingOnlyLimits,
    optimized64kAllocation,
  );
  const bufferOnlyPlan = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum64k,
    bufferOnlyLimits,
    optimized64kAllocation,
  );
  const bindingOnlyViolation = bindingOnlyPlan.limitStatus.violations[0];
  const bufferOnlyViolation = bufferOnlyPlan.limitStatus.violations[0];
  check('compact pool exact single-buffer limit should pass', exactPoolPlan.limitStatus.ok);
  check(
    'compact pool binding-only overflow classification changed',
    bindingOnlyPlan.limitStatus.violations.length === 1
      && bindingOnlyViolation?.bufferKey === 'pairContacts'
      && bindingOnlyViolation.exceedsMaxStorageBufferBindingSize
      && !bindingOnlyViolation.exceedsMaxBufferSize,
  );
  check(
    'compact pool maxBufferSize-only overflow classification changed',
    bufferOnlyPlan.limitStatus.violations.length === 1
      && bufferOnlyViolation?.bufferKey === 'pairContacts'
      && !bufferOnlyViolation.exceedsMaxStorageBufferBindingSize
      && bufferOnlyViolation.exceedsMaxBufferSize,
  );

  // Alias ownership is represented on pairBodyContactIndices; there must be no
  // second physical bodyConstraintRefs allocation in either domain.
  const aliased = optimized64k;
  const unaliased = planPhysicsEngineCapacity(
    CAPACITY_PLANNER_REFERENCE_PROFILES.coliseum64k,
    undefined,
    { ...optimized64kAllocation, contactOnlyAdjacencyAlias: false },
  );
  const aliasedGpuOwner = aliased.gpuBuffers.find(
    (buffer) => buffer.key === 'pairBodyContactIndices',
  );
  const aliasedCpuOwner = aliased.cpuBuffers.find(
    (buffer) => buffer.key === 'pairBodyContactIndices',
  );
  const unaliasedGpuRefs = unaliased.gpuBuffers.find(
    (buffer) => buffer.key === 'bodyConstraintRefs',
  );
  check(
    'contact-only GPU adjacency alias missing or duplicated',
    aliasedGpuOwner?.aliases?.filter((alias) => alias === 'bodyConstraintRefsAttr').length === 1
      && !aliased.gpuBuffers.some((buffer) => buffer.key === 'bodyConstraintRefs'),
  );
  check(
    'contact-only CPU adjacency alias missing or duplicated',
    aliasedCpuOwner?.aliases?.filter((alias) => alias === 'bodyConstraintRefsData').length === 1
      && !aliased.cpuBuffers.some((buffer) => buffer.key === 'bodyConstraintRefs'),
  );
  check(
    'contact-only adjacency alias GPU bytes were not counted exactly once',
    unaliasedGpuRefs !== undefined
      && unaliased.totalGpuBytes - aliased.totalGpuBytes === unaliasedGpuRefs.bytes,
  );

  const pairContactsCpu = optimized50k.cpuBuffers.find(
    (buffer) => buffer.key === 'pairContacts',
  );
  const pairContactsGpu = optimized50k.gpuBuffers.find(
    (buffer) => buffer.key === 'pairContacts',
  );
  check(
    'GPU-only contact pool CPU placeholder layout changed',
    pairContactsCpu?.gpuOnlyPlaceholder === true
      && pairContactsCpu.bytes === 16
      && pairContactsCpu.logicalElementCount === pairContactsGpu?.elementCount,
  );

  const allocated = planPhysicsEngineCapacity(
    {
      maxBodies: 32,
      maxJoints: 128,
      maxSprings: 256,
      maxIgnoredPairs: 0,
    },
    undefined,
    { allocatedJoints: 10, allocatedSprings: 20 },
  );
  check(
    'allocated joint capacity was not used for physical records',
    allocated.derived.allocatedJoints === 10
      && allocated.derived.storageMaxJoints === 10
      && allocated.gpuBuffers.find((buffer) => buffer.key === 'jointRecords')?.bytes
        === 10 * JOINT_RECORD_FLOATS * BYTES_PER_WORD,
  );
  check(
    'allocated spring capacity was not used for physical records',
    allocated.derived.allocatedSprings === 20
      && allocated.derived.storageMaxSprings === 20
      && allocated.gpuBuffers.find((buffer) => buffer.key === 'springRecords')?.bytes
        === 20 * SPRING_RECORD_FLOATS * BYTES_PER_WORD,
  );

  const liveRecordsLogicalGraph = planPhysicsEngineCapacity(
    {
      maxBodies: 32,
      maxJoints: 128,
      maxSprings: 256,
      maxIgnoredPairs: 0,
    },
    undefined,
    {
      allocatedJoints: 1,
      allocatedSprings: 1,
      constraintGraphUsesLogicalMax: true,
      gpuOnlyBufferKeys: ['jointRecords', 'springRecords'],
    },
  );
  check(
    'live record allocation changed logical hard-constraint graph budgets',
    liveRecordsLogicalGraph.derived.storageMaxJoints === 1
      && liveRecordsLogicalGraph.derived.storageMaxSprings === 1
      && liveRecordsLogicalGraph.derived.maxJointsPerBodySolver
        === DEFAULT_MAX_JOINTS_PER_BODY_SOLVER
      && liveRecordsLogicalGraph.derived.maxSpringsPerBodySolver
        === DEFAULT_MAX_SPRINGS_PER_BODY_SOLVER
      && liveRecordsLogicalGraph.derived.maxBodyConstraintOverflowNodes
        === liveRecordsLogicalGraph.derived.maxBodyContactOverflowNodes + 2 * (128 + 256),
  );
  check(
    'live GPU-only record attributes lost their placeholders or staging length',
    liveRecordsLogicalGraph.cpuBuffers.find((buffer) => buffer.key === 'jointRecords')?.bytes === 16
      && liveRecordsLogicalGraph.cpuBuffers.find((buffer) => buffer.key === 'springRecords')?.bytes === 16
      && liveRecordsLogicalGraph.gpuBuffers.find((buffer) => buffer.key === 'jointRecords')?.bytes
        === JOINT_RECORD_FLOATS * BYTES_PER_WORD
      && liveRecordsLogicalGraph.gpuBuffers.find((buffer) => buffer.key === 'springRecords')?.bytes
        === SPRING_RECORD_FLOATS * BYTES_PER_WORD,
  );

  const roundedPhysicalRecord = planPhysicsEngineCapacity(
    { maxBodies: 8, maxJoints: 3, maxSprings: 3, maxIgnoredPairs: 0 },
    undefined,
    { allocatedJoints: 4, allocatedSprings: 4 },
  );
  check(
    'power-of-two physical record headroom above logical admission was rejected',
    roundedPhysicalRecord.derived.allocatedJoints === 4
      && roundedPhysicalRecord.derived.allocatedSprings === 4,
  );

  let rejectedInvalidAlias = false;
  try {
    planPhysicsEngineCapacity(
      { maxBodies: 8, maxJoints: 1, maxSprings: 0 },
      undefined,
      { contactOnlyAdjacencyAlias: true },
    );
  } catch {
    rejectedInvalidAlias = true;
  }
  check('non-contact-only adjacency alias should be rejected', rejectedInvalidAlias);

  let rejectedLegacyCompactCapacity = false;
  try {
    planPhysicsEngineCapacity(
      { maxBodies: 8 },
      undefined,
      { mode: 'legacy', compactContactPoolCapacity: 1 },
    );
  } catch {
    rejectedLegacyCompactCapacity = true;
  }
  check('legacy mode should reject compactContactPoolCapacity', rejectedLegacyCompactCapacity);

  let rejectedOversizedCompactCapacity = false;
  try {
    planPhysicsEngineCapacity(
      { maxBodies: 8, maxPersistentManifolds: 8, pairManifoldSlots: 4 },
      undefined,
      { mode: 'optimized', compactContactPoolCapacity: 33 },
    );
  } catch {
    rejectedOversizedCompactCapacity = true;
  }
  check('compact pool capacity above the logical fixed-slot maximum should be rejected', rejectedOversizedCompactCapacity);

  return {
    ok: failures.length === 0,
    exactContactSoa: canonicalContactStorageSelfTest.coliseum510k,
    exactContactCsrGpu: contactOnlyCsrGpuSelfTest.coliseum510k,
    exactContactCsrGpuRuntime: contactOnlyCsrGpuRuntimeSelfTest.coliseum510k,
    exactWidePersistent: wideCompactSelfTest.coliseum510k,
    exactWideContactGeneration: wideContactGenerationSelfTest.coliseum510k,
    profiles,
    failures,
  };
}
